import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';

const AuthContext = createContext(null);

const WS_URL = () => {
  const token = localStorage.getItem('token');
  if (process.env.NODE_ENV === 'production') {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/ws?token=${token}`;
  }
  return `ws://localhost:4000/ws?token=${token}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [suspension,     setSuspension]     = useState(null);  // { active, expiresAt }
  const [adminData,      setAdminData]      = useState(null);  // latest WS snapshot
  const [notifications,  setNotifications]  = useState([]);    // bell: notificaciones DB (welcome, team_message, new_episode, …)
  const [fairyAlerts,    setFairyAlerts]    = useState([]);    // hada: eventos WS live efímeros (fairy_notification)
  const [fairyId,        setFairyId]        = useState(() => localStorage.getItem('fairyId') || 'aurora');

  const heartbeatRef = useRef(null);
  const wsRef        = useRef(null);

  // ── WebSocket — all logged-in users (admins also receive snapshot events) ────

  const connectWs = useCallback(() => {
    if (wsRef.current) return;
    const ws = new WebSocket(WS_URL());
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'snapshot') {
          setAdminData(data);
        } else if (data.event === 'fairy_notification') {
          // Alerta live de la hada: va a fairyAlerts (efímero, NO al bell)
          const normalized = {
            type:      data.type,
            payload:   data,
            read:      false,
            createdAt: new Date().toISOString(),
          };
          setFairyAlerts((prev) => [normalized, ...prev].slice(0, 20));
        } else if (data.event === 'pending_notifications') {
          // Push inicial de notificaciones no leídas: fusionar con las ya cargadas por REST
          setNotifications((prev) => {
            const ws = data.notifications || [];
            const wsIds = new Set(ws.map((n) => n.id));
            const merged = [...ws, ...prev.filter((n) => !wsIds.has(n.id))];
            return merged.slice(0, 30);
          });
        }
      } catch {}
    };
    ws.onclose = () => {
      wsRef.current = null;
    };
    ws.onerror = () => {
      ws.close();
    };
  }, []);

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // ── Heartbeat ───────────────────────────────────────────────────────────────

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) return;
    const tick = async () => {
      try {
        const base = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
        const res  = await fetch(`${base}/user/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 403 && data.suspended) {
          setSuspension({ active: true, expiresAt: data.expiresAt || null });
        } else if (data.action === 'suspended') {
          setSuspension({ active: true, expiresAt: null });
        }
      } catch {}
    };
    tick(); // immediate first tick
    heartbeatRef.current = setInterval(tick, 60_000);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (user) {
      startHeartbeat();
      connectWs(); // all logged-in users join for fairy alerts; admins also get snapshots

      const base = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const token = localStorage.getItem('token');

      // Cargar hada guardada
      fetch(`${base}/user/fairy`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((data) => {
          const id = data.fairyId || 'aurora';
          localStorage.setItem('fairyId', id);
          setFairyId(id);
        })
        .catch(() => {});

      // Cargar notificaciones del bell desde REST (últimos 7 días, leídas y no leídas)
      fetch(`${base}/user/notifications`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((data) => { if (Array.isArray(data)) setNotifications(data); })
        .catch(() => {});
    }
    return () => {
      stopHeartbeat();
      disconnectWs();
    };
  }, [user?.id]);

  // ── Auth actions ────────────────────────────────────────────────────────────

  const login = async (email, password) => {
    const { user, token } = await api.auth.login({ email, password });
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setSuspension(null);
    setUser(user);
  };

  const register = async (email, password, username, invitationCode) => {
    const { user, token } = await api.auth.register({ email, password, username, invitationCode });
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setSuspension(null);
    setUser(user);
  };

  const changeFairy = useCallback((id) => {
    localStorage.setItem('fairyId', id);
    setFairyId(id);
  }, []);

  const clearFairyAlerts = () => setFairyAlerts([]);

  const logout = () => {
    stopHeartbeat();
    disconnectWs();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('fairyId');
    setSuspension(null);
    setAdminData(null);
    setNotifications([]);
    setFairyAlerts([]);
    setFairyId('aurora');
    setUser(null);
  };

  const clearNotifications = async () => {
    // Mark all as read in DB; keep in local state so the panel can still show them
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await fetch(`${base}/user/notifications/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
    } catch {}
  };

  const updateUser = (partialData) => {
    setUser((prev) => {
      const updated = { ...prev, ...partialData };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, suspension, adminData, notifications, clearNotifications, fairyAlerts, clearFairyAlerts, fairyId, setFairyId: changeFairy }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
